import { useCallback } from "react";

import {
  faArrowDown,
  faChevronDown,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";

import { NO_FOLDER_FILTER } from "src/api";
import { useAddFlow } from "src/api/queries";
import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { GetFlowsApiV1FlowsGetOrderByEnum } from "src/clients/flow-api";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { CreateFlowFromTemplateModal } from "src/flowsOverview/CreateFlowFromTemplateModal";
import {
  EditFlowModal,
  EditFlowModalOutputs,
} from "src/flowsOverview/EditFlowModal";
import { useCreateFolder } from "src/flowsOverview/v2/folderQueries";
import { handleNoFolderId } from "src/flowsOverview/v2/handleNoFolderId";
import { useOrderFlowsBy } from "src/flowsOverview/v2/useOrderFlowsBy";
import { useCapabilities } from "src/hooks/useCapabilities";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { SubHeaderButton } from "src/layout/SubHeader";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useFolderIdSearchParam } from "src/router/SearchParams";
import { useUserData } from "src/store/AuthStore";

const ORDER_BY_KEYS_TO_DISPLAY: {
  [key in GetFlowsApiV1FlowsGetOrderByEnum]: string;
} = {
  updated_at_traversed: "Last edited",
  name: "Name",
} as const;

const useFlowHeaderActions = () => {
  const [orderFlowsBy, saveOrderFlowsBy] = useOrderFlowsBy();
  const orderActionItems: OptionsDropdownElement[] = Object.entries(
    ORDER_BY_KEYS_TO_DISPLAY,
  ).map(([enumKey, value]) => ({
    key: value,
    action: () => {
      saveOrderFlowsBy(enumKey as GetFlowsApiV1FlowsGetOrderByEnum);
    },
  }));

  const {
    isOpen: isCreateModalOpenNoCheck,
    openModal: createModalOpenNoCheck,
    closeModal: createModalCloseNoCheck,
  } = useModal();

  const { flows: flowPermissions } = useCapabilities();
  const createActionItems = flowPermissions.canCreate
    ? {
        isCreateModalOpen: isCreateModalOpenNoCheck,
        onCreateModalOpen: createModalOpenNoCheck,
        onCreateModalClose: createModalCloseNoCheck,
      }
    : {};

  return {
    orderFlowsBy,
    orderActionItems,
    ...createActionItems,
  };
};

const useAddFlowHandler = (workspaceId: string) => {
  const createFolder = useCreateFolder();
  const addFlow = useAddFlow();
  const { signed_in_user_id } = useUserData();
  const [_, setSearchParamFolderId] = useFolderIdSearchParam();

  return useCallback(
    async ({
      name,
      slug,
      wsId,
      description,
      orgId,
      folderSelection,
      reviewConfig,
      testRunConfig,
    }: EditFlowModalOutputs) => {
      let folderId: string | null = null;
      if (folderSelection.mode === "addFolder") {
        const newFolder = (
          await createFolder.mutateAsync({
            name: folderSelection.folderName,
            workspace_id: workspaceId,
          })
        ).data;
        folderId = newFolder.id;
      } else {
        folderId = folderSelection.folderId;
      }
      const createdFlow = await addFlow.mutateAsync({
        name,
        description: description ?? "",
        wsId,
        slug,
        flow_folder_id: folderId,
        initial_version_description: "Initial Version",
        initial_version_name: "v1.0",
        created_by_id: signed_in_user_id ?? "",
        review_configuration: reviewConfig?.requires_review
          ? reviewConfig
          : undefined,
        test_run_configuration: testRunConfig?.worker_count
          ? { worker_count: testRunConfig.worker_count }
          : undefined,
      });
      setSearchParamFolderId(folderId ?? NO_FOLDER_FILTER);
      await tracker.emit(
        trackingEvents.createFlow({
          organization_id: orgId,
          workspace_id: wsId,
          flow_id: createdFlow.id,
        }),
      );
    },
    [
      createFolder,
      addFlow,
      signed_in_user_id,
      setSearchParamFolderId,
      workspaceId,
    ],
  );
};

/* Figma: https://www.figma.com/design/ha0mHhxPb4q2WRLzw0BcIK/Information-architecture-and-consistency?node-id=2892-10906&t=tllkNYmvY8vsQ0av-4*/
export const FlowOverviewActionsHeader: React.FC<{
  organizationId: string;
  workspaceId: string;
}> = ({ organizationId, workspaceId }) => {
  const { orderFlowsBy, orderActionItems, ...createActionItemsDefault } =
    useFlowHeaderActions();
  const {
    isOpen: isCreateFromTemplateModalOpen,
    openModal: createFromTemplateModalOpen,
    closeModal: createFromTemplateModalClose,
  } = useModal();

  const [searchParamFolderId] = useFolderIdSearchParam();

  const addFlowHandler = useAddFlowHandler(workspaceId);
  return (
    <>
      <div
        className="flex h-full items-center gap-x-2 border-gray-200 border-l"
        data-loc="flow-overview-actions-header"
      >
        <OptionsDropdown
          elements={[{ key: "header", header: "Sort by" }, ...orderActionItems]}
        >
          <div className="flex h-full w-56 items-center gap-x-1 py-[5px] pr-2 pl-3">
            <Icon icon={faArrowDown} size="xs" />
            <span className="w-full text-left font-inter-medium-12px text-gray-700">
              {ORDER_BY_KEYS_TO_DISPLAY[orderFlowsBy]}
            </span>
            <Icon icon={faChevronDown} size="xs" />
          </div>
        </OptionsDropdown>
        {createActionItemsDefault.onCreateModalOpen &&
          (isFeatureFlagEnabled(FEATURE_FLAGS.templates) ? (
            <OptionsDropdown
              elements={[
                {
                  key: "From scratch",
                  action: createActionItemsDefault.onCreateModalOpen,
                },
                {
                  key: "From template",
                  action: createFromTemplateModalOpen,
                },
              ]}
            >
              <div className="h-13 w-13">
                <SubHeaderButton
                  dataLoc="add-new-flow"
                  icon={faPlus}
                  onClick={() => {}}
                  tooltip="Create Decision Flow"
                  useFullSpace
                />
              </div>
            </OptionsDropdown>
          ) : (
            <div className="h-13 w-13">
              <SubHeaderButton
                dataLoc="add-new-flow"
                icon={faPlus}
                tooltip="Create Decision Flow"
                onClick={createActionItemsDefault.onCreateModalOpen}
                useFullSpace
              />
            </div>
          ))}
      </div>
      {createActionItemsDefault.onCreateModalClose &&
        isFeatureFlagEnabled(FEATURE_FLAGS.templates) && (
          <CreateFlowFromTemplateModal
            handleClose={createFromTemplateModalClose}
            isOpen={isCreateFromTemplateModalOpen}
          />
        )}
      {createActionItemsDefault.onCreateModalClose && (
        <EditFlowModal
          initialFolderId={handleNoFolderId(searchParamFolderId)}
          mode="add"
          open={createActionItemsDefault.isCreateModalOpen}
          organizationId={organizationId}
          title="Create new Decision Flow"
          workspaceId={workspaceId}
          onClose={createActionItemsDefault.onCreateModalClose}
          onConfirm={addFlowHandler}
        />
      )}
    </>
  );
};
